import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface UserRef {
  _id: string;
  email?: string;
  name?: string;
}

export interface LeaveRequestItem {
  flaggedToDirector: boolean;
  isOverride: boolean;
  _id: string;
  user: UserRef;
  org: string;
  reason: string;
  fromDate: string;
  toDate: string;
  leaveType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
  approvals?: any[];
}

export type GetLeaveRequestsParams = {
  status?: string;
  year?: number | string;
  page?: number;
  limit?: number;
  userId?: string;
};

export type GetLeaveRequestsResponse = {
  success: boolean;
  count: number;
  total: number;
  page?: number;
  limit?: number;
  totalPages: number;
  data: LeaveRequestItem[];
};

const fetchLeaveRequests = async (
  params: GetLeaveRequestsParams
): Promise<GetLeaveRequestsResponse> => {
  const { userId, ...restParams } = params;
  const url = userId ? `/leaves/requests/${userId}` : `/leaves/requests`;
  const { data } = await customAxios.get<GetLeaveRequestsResponse>(
    url,
    {
      params: restParams,
    }
  );
  return data;
};

export const useGetLeaveRequests = (
  params: GetLeaveRequestsParams,
  enabled = true
) => {
  const queryKey = [
    "leaves",
    "requests",
    params.userId ?? "me",
    params.status ?? "all",
    params.year ?? "all",
    params.page ?? 1,
    params.limit ?? "all",
  ];

  return useQuery<GetLeaveRequestsResponse, AxiosError>({
    queryKey,
    queryFn: () => fetchLeaveRequests(params),
    enabled,
    onError: (error) => {
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to fetch leave requests",
        color: "red",
      });
    },
  });
};
